// Trade-In deduction formulas — tokenizer + evaluator.
//
// A deduction row of type "Formula" holds an Excel expression, e.g. "{Base Price}-20"
// or "CEILING({11}/2,-10)". The Result is no longer typed by hand (ERP: Initial Pricing
// formula auto calculate) — it is derived here.
//
// ── Why Excel semantics ──────────────────────────────────────────────────────
// The backend never evaluates a formula. On save it stores `result` verbatim
// (sales/tradein_views.py — `Decimal(str(ded_data.get('result') or 0))`), and on
// Excel export it rewrites the {…} placeholders into real cell references and hands
// the string to Excel:
//
//     CEILING({11}/2,-10)   ->   =CEILING(K2/2,-10)
//
// So Excel is the authority on what a formula means, this module has to agree with
// it, and the number it produces is the number that gets stored. CEILING is not
// optional: DED_DEFAULTS ships "CEILING({11}/2,-10)" as the default for deduction 10
// and 14, so it appears before a user types anything.
//
// ── Grammar (recursive descent, no eval — the string comes from a user) ──────
//
//   expr   := term (('+' | '-') term)*
//   term   := power (('*' | '/') power)*
//   power  := unary ('^' unary)*
//   unary  := ('+' | '-')* primary
//   primary:= NUMBER | REF | FUNC '(' [expr (',' expr)*] ')' | '(' expr ')'
//   REF    := '{' ('Base Price' | <deduction number>) '}'
//
// The deduction number is the row's `no` when the caller numbered the rows, else its
// definition id — see "Display numbers" below.
//
// A REF to another deduction row resolves to that row's own value, so a Formula row
// may chain off another Formula row. Cycles ({3} inside row 3, or A -> B -> A) are
// rejected rather than recursed into.

// `{Base Price}` is matched case-insensitively and whitespace-insensitively so
// "{base price}" behaves like the chip the designs show. Mirrors the backend's
// `ref_token.lower() == "base price"`.
const BASE_PRICE_REF = 'base price';

// Quotients are compared against integers with a tolerance before rounding. Deduction
// values are 2dp money and significances are typically 10, so -125/-10 must read as
// exactly 12.5 and -60/-10 as exactly 6 — binary floats don't guarantee either.
const EPS = 1e-9;

const isDigit = ch => ch >= '0' && ch <= '9';
const isIdentStart = ch => (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z');
const isIdentChar = ch => isIdentStart(ch) || isDigit(ch) || ch === '.' || ch === '_';

// ─── Excel rounding primitives ────────────────────────────────────────────────
// Excel rounds halves away from zero (ROUND(-1.5, 0) is -2, not -1), which is not
// what Math.round does for negatives — hence sign * round(abs).
const roundHalfAwayFromZero = x => Math.sign(x) * Math.round(Math.abs(x) + EPS);
const ceilQuotient  = q => Math.ceil(q - EPS);
const floorQuotient = q => Math.floor(q + EPS);

const scaleBy = digits => Math.pow(10, digits);

// CEILING / FLOOR / MROUND all reject a number and significance of opposite signs the
// way Excel does (CEILING(2.5, -2) is #NUM!), so they share this guard.
const requireSameSign = (name, n, s) => {
    if (n > 0 && s < 0) throw new Error(`${name} needs number and significance to share a sign`);
};

// Rounds `n` to a multiple of `s`, away from zero. Verified against the two results the
// backend ships alongside its own default formula: with {11} = -120, CEILING(-60,-10) is
// -60, and with {11} = -250, CEILING(-125,-10) is -130.
const excelCeiling = (n, s) => {
    if (s === 0 || n === 0) return 0;
    requireSameSign('CEILING', n, s);
    // Same sign: the quotient is positive, so round it up and re-apply the sign.
    // Opposite sign (n < 0 < s): ceil(n/s)*s is already Excel's toward-zero answer.
    return s < 0 ? -(ceilQuotient(n / s) * Math.abs(s)) : ceilQuotient(n / s) * s;
};

// Mirror of excelCeiling, toward zero instead of away from it.
const excelFloor = (n, s) => {
    if (s === 0 || n === 0) return 0;
    requireSameSign('FLOOR', n, s);
    return s < 0 ? -(floorQuotient(n / s) * Math.abs(s)) : floorQuotient(n / s) * s;
};

// ─── Function table ───────────────────────────────────────────────────────────
// Keyed by upper-cased name; `arity` is [min, max] with Infinity for variadic.
// Deliberately numeric-only: no IF and no comparison operators, so a formula using
// them is reported as an error rather than silently mis-evaluated.
const FUNCTIONS = {
    CEILING:    { arity: [2, 2], fn: (n, s) => excelCeiling(n, s) },
    FLOOR:      { arity: [2, 2], fn: (n, s) => excelFloor(n, s) },
    MROUND:     { arity: [2, 2], fn: (n, s) => {
        if (s === 0 || n === 0) return 0;
        requireSameSign('MROUND', n, s);
        return roundHalfAwayFromZero(n / s) * s;
    } },
    ROUND:      { arity: [1, 2], fn: (n, d = 0) => {
        const f = scaleBy(Math.trunc(d));
        return roundHalfAwayFromZero(n * f) / f;
    } },
    ROUNDUP:    { arity: [1, 2], fn: (n, d = 0) => {
        const f = scaleBy(Math.trunc(d));
        return (Math.sign(n) * ceilQuotient(Math.abs(n) * f)) / f;
    } },
    ROUNDDOWN:  { arity: [1, 2], fn: (n, d = 0) => {
        const f = scaleBy(Math.trunc(d));
        return (Math.sign(n) * floorQuotient(Math.abs(n) * f)) / f;
    } },
    // Excel's INT floors toward negative infinity: INT(-2.5) is -3. TRUNC chops
    // toward zero: TRUNC(-2.5) is -2. They are not interchangeable.
    INT:        { arity: [1, 1], fn: n => Math.floor(n + EPS) },
    TRUNC:      { arity: [1, 2], fn: (n, d = 0) => {
        const f = scaleBy(Math.trunc(d));
        return (Math.sign(n) * floorQuotient(Math.abs(n) * f)) / f;
    } },
    ABS:        { arity: [1, 1], fn: n => Math.abs(n) },
    // Excel's MOD takes the sign of the divisor: MOD(-3, 2) is 1, not -1.
    MOD:        { arity: [2, 2], fn: (n, d) => {
        if (d === 0) throw new Error('Division by zero');
        return n - d * Math.floor(n / d);
    } },
    MIN:        { arity: [1, Infinity], fn: (...a) => Math.min(...a) },
    MAX:        { arity: [1, Infinity], fn: (...a) => Math.max(...a) },
    SUM:        { arity: [1, Infinity], fn: (...a) => a.reduce((t, v) => t + v, 0) },
    POWER:      { arity: [2, 2], fn: (n, p) => Math.pow(n, p) },
    SQRT:       { arity: [1, 1], fn: n => {
        if (n < 0) throw new Error('SQRT of a negative number');
        return Math.sqrt(n);
    } },
};

// ─── Tokenizer ────────────────────────────────────────────────────────────────
// Returns an array of tokens, or throws on any character that can't start one.
function tokenize(text) {
    const tokens = [];
    let i = 0;
    while (i < text.length) {
        const ch = text[i];

        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i += 1; continue; }

        if ('+-*/^(),'.includes(ch)) { tokens.push({ type: ch }); i += 1; continue; }

        if (isDigit(ch) || ch === '.') {
            let j = i;
            let dots = 0;
            while (j < text.length && (isDigit(text[j]) || text[j] === '.')) {
                if (text[j] === '.') dots += 1;
                j += 1;
            }
            const raw = text.slice(i, j);
            // "1.2.3" and a bare "." are not numbers
            if (dots > 1 || raw === '.') throw new Error(`Invalid number "${raw}"`);
            tokens.push({ type: 'number', value: parseFloat(raw) });
            i = j;
            continue;
        }

        if (ch === '{') {
            const close = text.indexOf('}', i + 1);
            // An unclosed brace is the typo the "Error sample" design shows ("{Base Pric-20")
            if (close === -1) throw new Error('Unclosed "{" in formula');
            tokens.push({ type: 'ref', name: text.slice(i + 1, close).trim() });
            i = close + 1;
            continue;
        }

        if (isIdentStart(ch)) {
            let j = i;
            while (j < text.length && isIdentChar(text[j])) j += 1;
            tokens.push({ type: 'func', name: text.slice(i, j) });
            i = j;
            continue;
        }

        throw new Error(`Unexpected character "${ch}"`);
    }
    return tokens;
}

// ─── Parser / evaluator ───────────────────────────────────────────────────────
// Walks the token list once, evaluating as it goes. `resolveRef` turns a REF's
// inner text into a number and is responsible for its own error messages.
function evaluateTokens(tokens, resolveRef) {
    let pos = 0;
    const peek = () => tokens[pos];

    const parseExpr = () => {
        let left = parseTerm();
        while (peek() && (peek().type === '+' || peek().type === '-')) {
            const op = tokens[pos++].type;
            const right = parseTerm();
            left = op === '+' ? left + right : left - right;
        }
        return left;
    };

    const parseTerm = () => {
        let left = parsePower();
        while (peek() && (peek().type === '*' || peek().type === '/')) {
            const op = tokens[pos++].type;
            const right = parsePower();
            if (op === '*') {
                left *= right;
            } else {
                if (right === 0) throw new Error('Division by zero');
                left /= right;
            }
        }
        return left;
    };

    const parsePower = () => {
        const base = parseUnary();
        if (peek() && peek().type === '^') {
            pos += 1;
            // Right-associative, like Excel: 2^3^2 is 2^(3^2).
            return Math.pow(base, parsePower());
        }
        return base;
    };

    const parseUnary = () => {
        if (peek() && (peek().type === '+' || peek().type === '-')) {
            const op = tokens[pos++].type;
            const value = parseUnary();
            return op === '-' ? -value : value;
        }
        return parsePrimary();
    };

    const parseArgs = (name) => {
        if (!peek() || peek().type !== '(') throw new Error(`${name} needs "(" after it`);
        pos += 1;
        const args = [];
        if (peek() && peek().type === ')') { pos += 1; return args; }
        args.push(parseExpr());
        while (peek() && peek().type === ',') { pos += 1; args.push(parseExpr()); }
        if (!peek() || peek().type !== ')') throw new Error(`${name} is missing ")"`);
        pos += 1;
        return args;
    };

    const parsePrimary = () => {
        const tok = peek();
        if (!tok) throw new Error('Formula is incomplete');
        if (tok.type === 'number') { pos += 1; return tok.value; }
        if (tok.type === 'ref')    { pos += 1; return resolveRef(tok.name); }
        if (tok.type === 'func') {
            pos += 1;
            const name = tok.name.toUpperCase();
            const spec = FUNCTIONS[name];
            // Excel would render an unknown name as #NAME? — almost always a typo,
            // so it's reported rather than guessed at.
            if (!spec) throw new Error(`Unknown function "${tok.name}"`);
            const args = parseArgs(name);
            const [min, max] = spec.arity;
            if (args.length < min || args.length > max) {
                throw new Error(`${name} takes ${min === max ? min : `${min}-${max}`} argument(s)`);
            }
            return spec.fn(...args);
        }
        if (tok.type === '(') {
            pos += 1;
            const value = parseExpr();
            if (!peek() || peek().type !== ')') throw new Error('Missing ")"');
            pos += 1;
            return value;
        }
        throw new Error(`Unexpected "${tok.type}"`);
    };

    const result = parseExpr();
    // Anything left over means the expression didn't consume its own input,
    // e.g. "5 5" or "(1+2))" — treat it as malformed rather than silently truncating.
    if (pos < tokens.length) throw new Error('Unexpected trailing input');
    return result;
}

// ─── Display numbers ──────────────────────────────────────────────────────────
// Stored formulas reference a deduction by its *definition id*, matching the backend's
// `def_id_to_col_letter.get(int(ref_token))`, and ids never move. Users don't see ids:
// a deduction's ID on screen is its place in the list — the definition's sort_order —
// and they write formulas in those numbers. The two parted ways when ERP-5301 listed
// "Opened and Not Activated" (id 32) 2nd: "Within 3 Days" is still id 2 but shows as 3,
// so the stored CEILING({2}/2,-10) reads CEILING({3}/2,-10) on screen.
//
// So formula text is renumbered on the way to the screen and back on the way out.
// `numbers` maps one side to the other ({ [from]: to }); a {N} it doesn't name,
// {Base Price} and the spacing inside the braces are left exactly as they were.
export function renumberFormulaRefs(formula, numbers) {
    if (!formula || !numbers) return formula;
    return String(formula).replace(/\{(\s*)(\d+)(\s*)\}/g, (ref, before, n, after) =>
        numbers[n] === undefined ? ref : `{${before}${numbers[n]}${after}}`);
}

// { [definition id]: display number } from [[id, sort_order], ...]. null when a
// sort_order is missing or repeated — two rows can't share a number, and a formula
// renumbered through an ambiguous map would be stored pointing at the wrong row.
// Callers then show the ids themselves, as before.
export function deductionDisplayNumbers(pairs) {
    const numbers = {};
    const taken = new Set();
    for (const [id, sortOrder] of pairs) {
        const no = Number(sortOrder);
        if (!Number.isInteger(no) || no <= 0 || taken.has(no)) return null;
        taken.add(no);
        numbers[id] = no;
    }
    return numbers;
}

// ─── Row lookup ───────────────────────────────────────────────────────────────
// `{N}` is the number the user sees: the row's `no` where the caller numbered the
// rows (and renumbered their formulas to match), else its definition id.
function buildRefIndex(rows) {
    const byNo = {};
    (rows || []).forEach(row => { byNo[String(row.no ?? row.id)] = row; });
    return byNo;
}

// Resolves one row (Numeric or Formula) to a number, recursing through Formula
// references. `chain` carries the rows currently being resolved so a cycle is
// reported instead of overflowing the stack.
function resolveRow(row, ctx, chain) {
    if (row.dataType === 'Numeric') {
        const raw = String(row.value ?? '').trim();
        if (raw === '') throw new Error(`Deduction ${row.name || row.id} has no value`);
        const num = Number(raw.replace(/,/g, ''));
        if (!isFinite(num)) throw new Error(`Deduction ${row.name || row.id} is not a number`);
        return num;
    }
    if (row.dataType === 'Formula') {
        if (chain.has(row)) throw new Error('Circular formula reference');
        const next = new Set(chain);
        next.add(row);
        const raw = stripLeadingEquals(row.formula);
        if (raw === '') throw new Error(`Deduction ${row.name || row.id} has no formula`);
        return evaluateTokens(tokenize(raw), name => resolveName(name, ctx, next));
    }
    // N/A and WhatsApp rows hold no number, so they can't feed a calculation.
    throw new Error(`Deduction ${row.name || row.id} is not numeric`);
}

function resolveName(name, ctx, chain) {
    if (name.replace(/\s+/g, ' ').toLowerCase() === BASE_PRICE_REF) {
        if (!isFinite(ctx.basePrice)) throw new Error('Base Price is not set');
        return ctx.basePrice;
    }
    const row = ctx.byNo[name];
    // The Excel export path falls back to 0 for an unresolvable reference. That's
    // right for a bulk export that must not crash, but wrong for a field the user is
    // editing — a silent 0 becomes a silently wrong price — so this reports instead.
    if (!row) throw new Error(`Unknown reference "{${name}}"`);
    return resolveRow(row, ctx, chain);
}

// The backend tolerates a stored leading "=" (`formula_text.strip().lstrip("=")`)
// because the Excel upload path used to save one, so the same input is accepted here
// rather than failing a formula that round-trips fine through the backend.
const stripLeadingEquals = formula => String(formula ?? '').trim().replace(/^=+/, '').trim();

// ─── Public API ───────────────────────────────────────────────────────────────
// Evaluates `formula` for `row` against the sibling `rows` and the item's base
// price. Never throws: returns { ok: true, value } or { ok: false, error }, so
// callers can render the designs' "⚠ Error" state without a try/catch.
//
// `basePrice` is the raw Base Price field ('' / null when the item is on
// WhatsApp or hasn't been filled in) — a formula that needs it fails cleanly.
export function evaluateDeductionFormula(formula, { row, rows, basePrice }) {
    const raw = stripLeadingEquals(formula);
    if (raw === '') return { ok: false, error: 'Value is required', empty: true };

    const bp = String(basePrice ?? '').trim().replace(/,/g, '');
    const ctx = {
        byNo: buildRefIndex(rows),
        basePrice: bp === '' ? NaN : Number(bp),
    };

    try {
        const chain = new Set();
        if (row) chain.add(row);
        const value = evaluateTokens(tokenize(raw), name => resolveName(name, ctx, chain));
        if (!isFinite(value)) return { ok: false, error: 'Formula result is not a number' };
        return { ok: true, value };
    } catch (err) {
        return { ok: false, error: err.message || 'Invalid formula' };
    }
}

// Display form for a calculated result: whole numbers stay whole (the designs
// show "120", not "120.00"), fractions are capped at 2dp with trailing zeros
// trimmed so "119.5" doesn't render as "119.50".
export function formatFormulaResult(value) {
    if (!isFinite(value)) return '';
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : String(parseFloat(rounded.toFixed(2)));
}

const EMPTY_SET = new Set();

// Recomputes `result` for every Formula row in one pass. Called on commit — the
// spec is "show the result only when the user leaves the field" — and because a
// Numeric edit can change any Formula row that references it, every Formula row
// is refreshed, not just the one that was edited.
//
// Each Formula row comes back with:
//   result      — the calculated number as a string, '' when it can't be calculated
//   resultError — why it couldn't be, driving the "⚠ Error" cell
//
// ── `owned`: which rows this evaluator is allowed to fail ────────────────────
// This module implements a *subset* of Excel, but the stored formula text is
// unrestricted. The Excel upload path (`excel_formula_to_template`) whitelists
// nothing: it stores whatever the spreadsheet contained, and for a cross-row
// reference it keeps the expression verbatim with raw cell refs —
// "CEILING(K5/2,-10)". Those rows arrive with a perfectly good `result` that Excel
// itself calculated.
//
// So "this evaluator can't parse it" must not be reported as "this formula is wrong".
// `owned` is the set of row ids whose formula the user has edited in this session:
//
//   owned      — the user typed it, so the frontend owns the number. A formula that
//                won't evaluate is a genuine error and is surfaced as one.
//   not owned  — untouched backend data. A formula that won't evaluate keeps its
//                saved result and is left alone; only one that *does* evaluate gets
//                refreshed. Nothing loaded from the backend can show an error.
//
// An *empty* formula is always left with a blank resultError: whether emptiness is an
// error depends on whether the user has committed the field, which is a UI concern —
// so the caller decides, and turns it into the inline "Value is required".
export function recalcDeductionResults(rows, basePrice, owned = EMPTY_SET) {
    const invalid = {};
    const next = rows.map(row => {
        if (row.dataType !== 'Formula') return row;
        const outcome = evaluateDeductionFormula(row.formula, { row, rows, basePrice });
        if (!outcome.ok) {
            // Untouched and non-empty: the evaluator's limitation, not the user's mistake.
            // Keep Excel's saved result exactly as it came in.
            if (!owned.has(row.id) && !outcome.empty) return row;
            invalid[row.id] = outcome;
            const resultError = outcome.empty ? '' : outcome.error;
            if (row.result === '' && (row.resultError || '') === resultError) return row;
            return { ...row, result: '', resultError };
        }
        const result = formatFormulaResult(outcome.value);
        if (row.result === result && !row.resultError) return row;
        return { ...row, result, resultError: '' };
    });
    return { rows: next, invalid };
}

// Names accepted in a formula — surfaced so the UI can tell the user what's available
// instead of making them discover it by trial and error.
export const SUPPORTED_FUNCTIONS = Object.keys(FUNCTIONS).sort();
